import { useState, useMemo } from 'react';

interface UsePaginatedDataProps<T> {
  data: T[];
  searchFields: (keyof T)[];
  itemsPerPageDefault?: number;
  customFilters?: Record<string, (item: T, value: string) => boolean>;
}

interface UsePaginatedDataReturn<T> {
  // Pagination
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  paginatedData: T[];
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (items: number) => void;
  
  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  
  // Filtering
  filters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  
  // Filtered data (before pagination)
  filteredData: T[];
  totalFilteredItems: number;
}

export function usePaginatedData<T>({
  data,
  searchFields,
  itemsPerPageDefault = 10,
  customFilters = {},
}: UsePaginatedDataProps<T>): UsePaginatedDataReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageDefault);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Set individual filter
  const setFilter = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Update items per page and reset to first page
  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Update search query and reset to first page
  const handleSearchQueryChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    let result = data;

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item =>
        searchFields.some(field => {
          const value = item[field];
          if (value == null) return false;
          return String(value).toLowerCase().includes(query);
        })
      );
    }

    // Apply filters (both direct property matching and custom filters)
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (customFilters[key]) {
          // Use custom filter function
          result = result.filter(item => customFilters[key](item, value));
        } else {
          // Direct property matching
          result = result.filter(item => {
            const itemValue = item[key as keyof T];
            return itemValue === value;
          });
        }
      }
    });

    return result;
  }, [data, searchQuery, searchFields, filters, customFilters]);

  // Calculate pagination
  const totalFilteredItems = filteredData.length;
  const totalPages = Math.ceil(totalFilteredItems / itemsPerPage);
  
  // Ensure current page is valid
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages || 1);

  // Get paginated data
  const paginatedData = useMemo(() => {
    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, validCurrentPage, itemsPerPage]);

  // Update current page if it becomes invalid
  if (validCurrentPage !== currentPage) {
    setCurrentPage(validCurrentPage);
  }

  return {
    // Pagination
    currentPage: validCurrentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    setCurrentPage,
    setItemsPerPage: handleItemsPerPageChange,
    
    // Search
    searchQuery,
    setSearchQuery: handleSearchQueryChange,
    
    // Filtering
    filters,
    setFilter,
    clearFilters,
    
    // Data
    filteredData,
    totalFilteredItems,
  };
}